import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "./AuthContext";
import {
  LogOut,
  LayoutDashboard,
  Package,
  Route as RouteIcon,
  Users,
  Store,
  Mail,
  Settings,
} from "lucide-react";
import SummaryPage from "./pages/SummaryPage";
import OrdersPage from "./pages/OrdersPage";
import MessagesPage from "./pages/MessagesPage";
import ProductsPage from "./pages/ProductsPage";
import ComingSoonPage from "./pages/ComingSoonPage";

const TABS = [
  { id: "summary", label: "Resumo", icon: LayoutDashboard },
  { id: "orders", label: "Encomendas", icon: Package, badgeKey: "orders_new" },
  { id: "routes", label: "Rotas", icon: RouteIcon },
  { id: "clients", label: "Clientes", icon: Users },
  { id: "shop", label: "Loja / Produtos", icon: Store },
  { id: "messages", label: "Mensagens", icon: Mail, badgeKey: "messages_new" },
  { id: "settings", label: "Definições", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const [tab, setTab] = useState("summary");
  const [presetFilters, setPresetFilters] = useState(null);
  const [notifications, setNotifications] = useState({ orders_new: 0, messages_new: 0 });
  const timerRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications`, { headers: authHeaders() });
      setNotifications(data);
    } catch {
      /* silent */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadNotifications();
    timerRef.current = setInterval(loadNotifications, 30000);
    return () => clearInterval(timerRef.current);
  }, [loadNotifications]);

  const markSeen = async (kind) => {
    try {
      await axios.post(
        `${API}/admin/notifications/seen/${kind}`,
        {},
        { headers: authHeaders() }
      );
      setNotifications((n) => ({ ...n, [`${kind}_new`]: 0 }));
    } catch {
      /* silent */
    }
  };

  const goToOrders = (filters = null) => {
    setPresetFilters(filters);
    setTab("orders");
    markSeen("orders");
  };

  const onTabClick = (t) => {
    setTab(t.id);
    if (t.id !== "orders") setPresetFilters(null);
    if (t.id === "orders") markSeen("orders");
    if (t.id === "messages") markSeen("messages");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F3F1EB]">
      <header className="bg-[#3A4A38] text-white">
        <div className="container-custom flex items-center justify-between py-4">
          <div>
            <h1 className="text-xl font-semibold">Painel da Quinta</h1>
            <p className="text-white/70 text-xs">{user?.email}</p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#D4C4A8] text-[#3A4A38] font-medium text-sm hover:bg-[#E8DEC8] transition-colors"
          >
            <LogOut size={14} />
            Sair
          </button>
        </div>
      </header>

      <nav className="bg-white border-b border-[#D4C4A8]/50 sticky top-0 z-20">
        <div className="container-custom flex gap-1 overflow-x-auto scrollbar-thin">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            const badge = t.badgeKey ? notifications[t.badgeKey] : 0;
            return (
              <button
                key={t.id}
                onClick={() => onTabClick(t)}
                data-testid={`admin-tab-${t.id}`}
                className={`relative inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? "border-[#3A4A38] text-[#3A4A38]"
                    : "border-transparent text-[#2C2C2C]/60 hover:text-[#2C2C2C]"
                }`}
              >
                <Icon size={16} />
                {t.label}
                {badge > 0 && (
                  <span
                    data-testid={`admin-tab-badge-${t.id}`}
                    className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="container-custom py-8">
        {tab === "summary" && <SummaryPage onNavigateToOrders={goToOrders} />}
        {tab === "orders" && (
          <OrdersPage presetFilters={presetFilters} onClearPreset={() => setPresetFilters(null)} />
        )}
        {tab === "routes" && (
          <ComingSoonPage
            title="Rotas de Entrega"
            description="Em breve poderá criar rotas, agrupar encomendas por zona, ordenar paragens, abrir no Google Maps e marcar entregas."
            phase="Fase 3 — em planeamento"
            testId="admin-routes-page"
          />
        )}
        {tab === "clients" && (
          <ComingSoonPage
            title="Clientes"
            description="Em breve, base de clientes gerada automaticamente a partir das encomendas, com histórico, preferências e observações internas."
            phase="Fase 3 — em planeamento"
            testId="admin-clients-page"
          />
        )}
        {tab === "shop" && <ProductsPage />}
        {tab === "messages" && <MessagesPage />}
        {tab === "settings" && (
          <ComingSoonPage
            title="Definições"
            description="Em breve, gestão de utilizadores, perfis (administrador, gestão de produtos, preparação, entregador), credenciais e preferências."
            phase="Fase 4 — futura"
            testId="admin-settings-page"
          />
        )}
      </main>
    </div>
  );
}
